import type { HTMLAttributes, ReactNode } from 'react';
import { ArrowLeftIcon } from '@/components/ui/arrow-left';
import { BanIcon } from '@/components/ui/ban';
import { ChevronLeftIcon } from '@/components/ui/chevron-left';
import { ChevronRightIcon } from '@/components/ui/chevron-right';
import { CircleCheckIcon } from '@/components/ui/circle-check';
import { CopyIcon } from '@/components/ui/copy';
import { DeleteIcon } from '@/components/ui/delete';
import { DownloadIcon } from '@/components/ui/download';
import { EyeIcon } from '@/components/ui/eye';
import { LayoutGridIcon } from '@/components/ui/layout-grid';
import { LoaderCircleIcon } from '@/components/ui/loader-circle';
import { LockIcon } from '@/components/ui/lock';
import { LogInIcon } from '@/components/ui/login';
import { MenuIcon } from '@/components/ui/menu';
import { PlusIcon } from '@/components/ui/plus';
import { RefreshCWIcon } from '@/components/ui/refresh-cw';
import { SearchIcon } from '@/components/ui/search';
import { SendIcon } from '@/components/ui/send';
import { SettingsIcon } from '@/components/ui/settings';
import { SquarePenIcon } from '@/components/ui/square-pen';
import { UploadIcon } from '@/components/ui/upload';
import { UserPlusIcon } from '@/components/ui/user-plus';
import { XIcon } from '@/components/ui/x';
import { cn } from '@/lib/utils';

export type ButtonIconKey =
  | 'plus'
  | 'x'
  | 'circle-check'
  | 'eye'
  | 'square-pen'
  | 'delete'
  | 'chevron-left'
  | 'chevron-right'
  | 'login'
  | 'user-plus'
  | 'download'
  | 'upload'
  | 'refresh-cw'
  | 'search'
  | 'arrow-left'
  | 'layout-grid'
  | 'menu'
  | 'lock'
  | 'send'
  | 'settings'
  | 'copy'
  | 'ban'
  | 'loader-circle';

type AnimatedIconProps = HTMLAttributes<HTMLDivElement> & { size?: number };

const ICON_COMPONENTS: Record<ButtonIconKey, React.ComponentType<AnimatedIconProps>> = {
  plus: PlusIcon,
  x: XIcon,
  'circle-check': CircleCheckIcon,
  eye: EyeIcon,
  'square-pen': SquarePenIcon,
  delete: DeleteIcon,
  'chevron-left': ChevronLeftIcon,
  'chevron-right': ChevronRightIcon,
  login: LogInIcon,
  'user-plus': UserPlusIcon,
  download: DownloadIcon,
  upload: UploadIcon,
  'refresh-cw': RefreshCWIcon,
  search: SearchIcon,
  'arrow-left': ArrowLeftIcon,
  'layout-grid': LayoutGridIcon,
  menu: MenuIcon,
  lock: LockIcon,
  send: SendIcon,
  settings: SettingsIcon,
  copy: CopyIcon,
  ban: BanIcon,
  'loader-circle': LoaderCircleIcon,
};

/** i18n keys from `shared.actions.*` */
const ACTION_KEY_TO_ICON: Record<string, ButtonIconKey> = {
  view: 'eye',
  edit: 'square-pen',
  delete: 'delete',
  create: 'plus',
  new: 'plus',
  save: 'circle-check',
  cancel: 'x',
  search: 'search',
  back: 'arrow-left',
  invite: 'user-plus',
};

const TEXT_ICON_RULES: Array<{ test: RegExp; icon: ButtonIconKey }> = [
  { test: /^(previous|prev)$/i, icon: 'chevron-left' },
  { test: /^(next)$/i, icon: 'chevron-right' },
  { test: /^(view|xem)$/i, icon: 'eye' },
  { test: /^(edit|sửa)$/i, icon: 'square-pen' },
  { test: /^(delete|xóa)$/i, icon: 'delete' },
  { test: /^(cancel|close|hủy|đóng)$/i, icon: 'x' },
  { test: /^(save|lưu|update|cập nhật)$/i, icon: 'circle-check' },
  { test: /^(create|creating|tạo)/i, icon: 'plus' },
  { test: /^(new|thêm)/i, icon: 'plus' },
  { test: /^(search|tìm)/i, icon: 'search' },
  { test: /^(back|quay lại)$/i, icon: 'arrow-left' },
  { test: /^(invite|mời)/i, icon: 'user-plus' },
  { test: /^(sign in|signing in|log in|đăng nhập)/i, icon: 'login' },
  { test: /^(register|sign up|signing up|đăng ký)/i, icon: 'user-plus' },
  { test: /^(copy link|copy)/i, icon: 'copy' },
  { test: /^(publish|publishing|phát hành)/i, icon: 'upload' },
  { test: /^(send reset|sending|gửi liên kết)/i, icon: 'send' },
  { test: /^(list)$/i, icon: 'menu' },
  { test: /^(card|kanban)$/i, icon: 'layout-grid' },
  { test: /^(clone|nhân bản|duplicate)/i, icon: 'copy' },
  { test: /^(deactivate|vô hiệu|disable)/i, icon: 'ban' },
  { test: /^(download|tải xuống)/i, icon: 'download' },
  { test: /^(upload|tải lên)/i, icon: 'upload' },
  { test: /upload plugin/i, icon: 'upload' },
  { test: /^add$/i, icon: 'plus' },
  { test: /^x$/i, icon: 'x' },
  { test: /^(refresh|reload|làm mới)/i, icon: 'refresh-cw' },
  { test: /^(assign|gán)/i, icon: 'send' },
  { test: /^(password|mật khẩu)/i, icon: 'lock' },
  { test: /^(apply|áp dụng)/i, icon: 'circle-check' },
  { test: /^(clear|reset|xóa bộ lọc)/i, icon: 'x' },
  { test: /^(remove)/i, icon: 'x' },
  { test: /^(settings|cài đặt)/i, icon: 'settings' },
  { test: /(…|\.\.\.)$/i, icon: 'loader-circle' },
];

export function extractButtonLabel(children: ReactNode): string {
  if (children == null || typeof children === 'boolean') return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children).trim();
  if (Array.isArray(children)) return children.map(extractButtonLabel).join('').trim();
  if (typeof children === 'object' && 'props' in children) {
    const props = children.props as { children?: ReactNode };
    return extractButtonLabel(props.children);
  }
  return '';
}

export function resolveButtonIcon(options: {
  icon?: ButtonIconKey | 'auto' | false;
  actionKey?: string;
  children?: ReactNode;
  size?: 'default' | 'sm' | 'lg' | 'icon';
}): ButtonIconKey | null {
  const { icon = 'auto', actionKey, children, size } = options;
  if (icon === false || size === 'icon') return null;
  if (icon && icon !== 'auto') return icon;
  if (actionKey && ACTION_KEY_TO_ICON[actionKey]) return ACTION_KEY_TO_ICON[actionKey];

  const label = extractButtonLabel(children);
  if (!label) return null;
  if (/^\d+$/.test(label)) return null;

  const normalized = label.replace(/\s+/g, ' ').trim();
  for (const rule of TEXT_ICON_RULES) {
    if (rule.test.test(normalized)) return rule.icon;
  }
  if (/^[\u2715×✕]$/.test(normalized)) return 'x';

  return null;
}

export function ButtonLeadingIcon({
  name,
  className,
  size = 16,
}: {
  name: ButtonIconKey;
  className?: string;
  size?: number;
}) {
  const Icon = ICON_COMPONENTS[name];
  return (
    <Icon
      size={size}
      className={cn('shrink-0 pointer-events-none', className)}
      aria-hidden
    />
  );
}
